import { useState, useEffect, useCallback } from 'react'
import {
  Card, Typography, Form, Input, Select, Button, Slider, Divider, Tag, message, Modal,
  Space, Switch, Descriptions, Popconfirm, Empty, Alert, InputNumber, AutoComplete,
} from 'antd'
import {
  ApiOutlined, CheckCircleOutlined, CloseCircleOutlined, QuestionCircleOutlined,
  KeyOutlined, LinkOutlined, SettingOutlined, CheckOutlined,
} from '@ant-design/icons'
import * as llmApi from '@/api/llmConfig'
import type { LLMConfigItem } from '@/api/llmConfig'
import { useLLMStore } from '@/stores/llm'
import { useAuthStore } from '@/stores/auth'
import '../../../styles/aurora-page.css'
import '../../../styles/aurora-modal.css'

const { Title, Text, Paragraph } = Typography
const { Option } = Select

/** 各厂商默认接入地址（模型名不设预设——更新太快易过时，由用户手输或从 API 获取） */
const providerOptions: Record<string, { label: string; defaultUrl: string }> = {
  openai: {
    label: 'OpenAI',
    defaultUrl: 'https://api.openai.com/v1',
  },
  azure: {
    label: 'Azure OpenAI',
    defaultUrl: 'https://YOUR-RESOURCE.openai.azure.com',
  },
  deepseek: {
    label: 'DeepSeek',
    defaultUrl: 'https://api.deepseek.com/v1',
  },
  qwen: {
    label: '通义千问',
    defaultUrl: 'https://dashscope.aliyuncs.com/compatible-mode/v1',
  },
  zhipu: {
    label: '智谱 GLM',
    defaultUrl: 'https://open.bigmodel.cn/api/paas/v4',
  },
  moonshot: {
    label: 'Moonshot (月之暗面)',
    defaultUrl: 'https://api.moonshot.cn/v1',
  },
  custom: {
    label: '自定义',
    defaultUrl: '',
  },
}

const statusIcon: Record<string, React.ReactNode> = {
  success: <CheckCircleOutlined style={{ color: '#10b981' }} />,
  fail: <CloseCircleOutlined style={{ color: '#ef4444' }} />,
  unknown: <QuestionCircleOutlined style={{ color: 'var(--au-slate-muted)' }} />,
}

export default function LLMConfig() {
  const [configs, setConfigs] = useState<LLMConfigItem[]>([])
  const [loading, setLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [testingId, setTestingId] = useState<number | null>(null)
  const [form] = Form.useForm()
  const [selectedProvider, setSelectedProvider] = useState('openai')
  const [fetchingModels, setFetchingModels] = useState(false)
  const [dynamicModels, setDynamicModels] = useState<string[]>([])

  const { setActiveConfig } = useLLMStore()
  const { user } = useAuthStore()
  // 后端 AI 配置写操作仅 admin 开放，前端同步隐藏相关按钮
  const isAdmin = user?.role === 1

  const fetchList = useCallback(async () => {
    setLoading(true)
    try {
      const res = await llmApi.listConfigs()
      const list = res || []
      setConfigs(list)
      // 自动设置第一个活跃的配置为全局配置
      const active = list.find((c) => c.is_active)
      if (active) setActiveConfig(active)
    } finally {
      setLoading(false)
    }
  }, [setActiveConfig])

  useEffect(() => {
    fetchList()
  }, [fetchList])

  const handleCreate = () => {
    setEditingId(null)
    setSelectedProvider('openai')
    setDynamicModels([])
    form.resetFields()
    form.setFieldsValue({
      provider: 'openai',
      base_url: providerOptions.openai.defaultUrl,
      max_tokens: 32768,
      temperature: 0.7,
      top_p: 1.0,
      extra_config: '{}',
    })
    setModalOpen(true)
  }

  const handleEdit = (record: LLMConfigItem) => {
    setEditingId(record.id)
    setSelectedProvider(record.provider)
    setDynamicModels([])
    form.setFieldsValue(record)
    setModalOpen(true)
  }

  const handleDelete = async (id: number) => {
    try {
      await llmApi.deleteConfig(id)
      message.success('删除成功')
      fetchList()
    } catch (e) { console.error('[LLMConfig] 操作失败:', e) }
  }

  const handleProviderChange = (provider: string) => {
    setSelectedProvider(provider)
    setDynamicModels([])
    const opt = providerOptions[provider]
    if (opt) {
      form.setFieldValue('base_url', opt.defaultUrl)
    }
    // 模型名清空：不同厂商模型不通用，由用户重新手输或获取
    form.setFieldValue('model_name', undefined)
  }

  /** 调用后端从所选提供商 API 获取模型列表 */
  const handleFetchModels = async () => {
    const provider = form.getFieldValue('provider') || 'openai'
    const baseUrl = form.getFieldValue('base_url')
    const apiKey = String(form.getFieldValue('api_key') || '')

    if (!baseUrl) {
      message.warning('请先填写 API 地址')
      return
    }

    setFetchingModels(true)
    try {
      // 编辑时表单中的 Key 是脱敏值（含 *），明文请求必然 401 → 改走后端已保存密钥通道
      const masked = apiKey.includes('*')
      const res = masked && editingId
        ? await llmApi.fetchModelsSaved(editingId, { base_url: baseUrl })
        : await llmApi.fetchModels({ provider, base_url: baseUrl, api_key: apiKey })
      if (res && Array.isArray(res)) {
        setDynamicModels(res)
        message.success(`获取到 ${res.length} 个模型`)
      }
    } finally {
      setFetchingModels(false)
    }
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      setSubmitting(true)
      if (editingId) {
        await llmApi.updateConfig(editingId, values)
        message.success('配置更新成功')
      } else {
        const res = await llmApi.createConfig(values)
        // 新配置默认设为激活
        if (res) {
          await llmApi.updateConfig(res.id, { is_active: true })
        }
        message.success('配置创建成功')
      }
      setModalOpen(false)
      fetchList()
    } catch {
    } finally {
      setSubmitting(false)
    }
  }

  const handleTestConnection = async (id: number) => {
    setTestingId(id)
    try {
      const res = await llmApi.testConnection(id)
      // 使用后端返回的 message，不前端硬编码
      Modal.success({
        title: '连接成功',
        content: (
          <div>
            <p>{res.message}</p>
            {res.data?.reply && (
              <Alert type="success" message={`模型回复: "${res.data.reply}"`} style={{ marginTop: 8 }} />
            )}
          </div>
        ),
      })
      fetchList()
    } catch {
      Modal.error({ title: '连接失败', content: '请检查 API 地址和 Key 是否正确' })
    } finally {
      setTestingId(null)
    }
  }

  const handleToggleActive = async (record: LLMConfigItem) => {
    try {
      await llmApi.updateConfig(record.id, { is_active: !record.is_active })
      message.success(record.is_active ? '已禁用' : '已启用')
      fetchList()
    } catch (e) { console.error('[LLMConfig] 操作失败:', e) }
  }

  return (
    <div className="aurora-page">
      <div className="page-header">
        <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
          AI配置
        </Title>
        <Space>
          <Button onClick={fetchList}>刷新</Button>
          {isAdmin && (
            <Button type="primary" onClick={handleCreate}>
              添加配置
            </Button>
          )}
        </Space>
      </div>

      <div className="content-card" style={{ padding: 24 }}>
        {!loading && configs.length === 0 ? (
          <Empty description="暂无AI配置，点击上方按钮添加" style={{ padding: 60 }} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {configs.map((cfg) => (
              <Card
                key={cfg.id}
                size="small"
                style={{
                  border: cfg.is_active ? '1px solid var(--au-primary)' : '1px solid var(--border-light)',
                  boxShadow: cfg.is_active ? '0 0 0 2px var(--primary-bg)' : undefined,
                }}
                title={
                  <Space>
                    {statusIcon[cfg.last_test_result]}
                    <Text strong style={{ fontSize: 15 }}>{cfg.description || '未命名配置'}</Text>
                    {cfg.is_active ? (
                      <Tag color="success" style={{ marginLeft: 4 }}>启用中</Tag>
                    ) : (
                      <Tag style={{ marginLeft: 4 }}>已禁用</Tag>
                    )}
                  </Space>
                }
                extra={
                  isAdmin ? (
                    <Space>
                      <Switch
                        checked={cfg.is_active}
                        onChange={() => handleToggleActive(cfg)}
                        checkedChildren="启用"
                        unCheckedChildren="禁用"
                      />
                      <Button size="small" onClick={() => handleTestConnection(cfg.id)}
                        loading={testingId === cfg.id} type="link">
                        测试连接
                      </Button>
                      <Button size="small" onClick={() => handleEdit(cfg)} type="link">
                        编辑
                      </Button>
                      <Popconfirm
                        title="确定删除此配置？"
                        onConfirm={() => handleDelete(cfg.id)}
                        okText="确定"
                        cancelText="取消"
                        okButtonProps={{ danger: true }}
                      >
                        <Button size="small" danger type="link">删除</Button>
                      </Popconfirm>
                    </Space>
                  ) : null
                }
              >
                <Descriptions column={3} size="small">
                  <Descriptions.Item label={<><ApiOutlined /> 提供商</>}>
                    <Tag color="blue">{getProviderLabel(cfg.provider)}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label={<><SettingOutlined /> 模型</>}>
                    <code>{cfg.model_name}</code>
                  </Descriptions.Item>
                  <Descriptions.Item label={<><LinkOutlined /> Base URL</>}>
                    <Text copyable style={{ fontSize: 12 }}>{cfg.base_url}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label={<><KeyOutlined /> API Key</>}>
                    <Text style={{ fontFamily: 'monospace' }}>{cfg.api_key || '未设置'}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="温度">{cfg.temperature}</Descriptions.Item>
                  <Descriptions.Item label="Max Tokens">{cfg.max_tokens}</Descriptions.Item>
                </Descriptions>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* 新增/编辑弹窗 */}
      <Modal
        title={editingId ? '编辑AI配置' : '添加AI配置'}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        confirmLoading={submitting}
        width={680}
        okText="保存"
        cancelText="取消"
        wrapClassName="aurora-modal-wrap"
        className="aurora-modal"
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          {/* 基本信息 */}
          <Form.Item name="provider" label="提供商" rules={[{ required: true }]}>
            <Select onChange={handleProviderChange}>
              {Object.entries(providerOptions).map(([k, v]) => (
                <Option key={k} value={k}>{v.label}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="description" label="配置说明">
            <Input placeholder="如：生产环境 GPT-4o、测试用 DeepSeek" />
          </Form.Item>

          {/* 连接配置 */}
          <Divider orientation="left" plain style={{ fontSize: 13 }}>连接配置</Divider>

          <Form.Item name="base_url" label="API 接口地址" rules={[{ required: true, message: '请输入 Base URL' }]}>
            <Input placeholder="https://api.openai.com/v1" prefix={<LinkOutlined />} />
          </Form.Item>

          <Form.Item
            name="api_key"
            label="API Key"
            rules={[{ required: true, message: '请输入 API Key' }]}
            extra="编辑时显示为脱敏值：不修改则保留原 Key，「获取模型」也会自动使用已保存的 Key"
          >
            <Input.Password placeholder="sk-..." prefix={<KeyOutlined />} />
          </Form.Item>

          <Form.Item name="api_secret" label="API Secret（部分厂商需要）">
            <Input.Password placeholder="可选" />
          </Form.Item>

          {/* 模型参数 */}
          <Divider orientation="left" plain style={{ fontSize: 13 }}>模型参数</Divider>

          <Form.Item
            label="模型名称"
            required
            style={{ marginBottom: 8 }}
            extra="支持手动输入任意模型名（含列表未收录的新模型）；也可先填好地址和 Key，点击「获取模型」后从返回列表中选择"
          >
            <Space.Compact block>
              <Form.Item name="model_name" noStyle rules={[{ required: true, message: '请输入或选择模型名称' }]}>
                <AutoComplete
                  style={{ width: '100%' }}
                  options={dynamicModels.map((m) => ({ value: m }))}
                  placeholder="手动输入模型名，如：glm-5.3-flash"
                />
              </Form.Item>
              <Button onClick={handleFetchModels} loading={fetchingModels} style={{ width: 100 }}>
                获取模型
              </Button>
            </Space.Compact>
          </Form.Item>

          <Space style={{ width: '100%' }} size="large">
            <Form.Item name="max_tokens" label="最大输出 Token 数">
              <InputNumber min={1} max={128000} step={1024} style={{ width: 160 }} />
            </Form.Item>
          </Space>

          <Form.Item label="温度 (Temperature)" style={{ marginBottom: 0 }}>
            <Space style={{ width: '100%' }}>
              <Form.Item name="temperature" noStyle>
                <Slider min={0} max={2} step={0.1} style={{ width: 280 }} />
              </Form.Item>
              <Form.Item name="temperature" noStyle rules={[{ required: true }]}>
                <InputNumber min={0} max={2} step={0.1} style={{ width: 64 }} />
              </Form.Item>
            </Space>
            <Text type="secondary" style={{ fontSize: 12 }}>值越高回复越随机，越低越确定</Text>
          </Form.Item>

          <Form.Item label="Top P" style={{ marginBottom: 0 }}>
            <Space style={{ width: '100%' }}>
              <Form.Item name="top_p" noStyle>
                <Slider min={0} max={1} step={0.05} style={{ width: 280 }} />
              </Form.Item>
              <Form.Item name="top_p" noStyle>
                <InputNumber min={0} max={1} step={0.05} style={{ width: 64 }} />
              </Form.Item>
            </Space>
          </Form.Item>

          {/* 高级配置 */}
          <Divider orientation="left" plain style={{ fontSize: 13 }}>高级配置</Divider>

          <Form.Item name="system_prompt" label="系统提示词">
            <Input.TextArea rows={3} placeholder="可设置全局系统提示词，所有 AI 调用默认生效" />
          </Form.Item>

          <Form.Item name="extra_config" label="扩展配置 (JSON)"
            rules={[{
              validator: (_, val) => {
                if (!val) return Promise.resolve()
                try { JSON.parse(val); return Promise.resolve() } catch { return Promise.reject('JSON 格式无效') }
              },
            }]}>
            <Input.TextArea rows={2} placeholder='{"proxy": "http://127.0.0.1:7890", "timeout": 30}' />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

/** 获取提供商中文标签 */
function getProviderLabel(provider: string): string {
  return providerOptions[provider]?.label || provider
}
